import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HeartPanel from "../components/notebook/HeartPanel";
import {
  SearchX,
  NotebookPen,
  Plus,
  Search,
  Trash2,
  Heart,
  HelpCircle,
  Sparkles,
  BookMarked,
  Edit3,
  ExternalLink,
  Loader2,
  X,
  Check,
  Flame,
  ScrollText,
  Save,
  Heading2,
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Highlighter,
  BookOpen,
  Wrench,
  MessageCircleHeart,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { api, bookUrl } from "../lib/api";
import { numberToHebrewLetter } from "../lib/hebrewNumerals";
import { digitsToHebrewGematria } from "../lib/hebrewGematria";
import { toast } from "sonner";
import "../components/notebook/notebook.css";

// TODO(layout): At 768px the top nav crowds "בית המדרש שלי" into 3 lines.
//               Fix the global Header nav crowding — separate task.

const NOTEBOOK_TAB_KEY = "tanakh_notebook_active_tab";
const TAB_IDS = ["entries", "heart"];

const SECTION_ICONS = {
  Heart, HelpCircle, Sparkles, BookMarked, NotebookPen,
};

const COLOR_OPTIONS = [
  { id: "amber",   he: "זהב"   },
  { id: "rose",    he: "ורד"   },
  { id: "sky",     he: "תכלת"  },
  { id: "emerald", he: "ירוק"  },
  { id: "violet",  he: "סגול"  },
  { id: "slate",   he: "אפור"  },
];

export default function Notebook() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [sections, setSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);
  const [flipState, setFlipState] = useState("idle"); // idle | out | in
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // {id, ...} or null for new
  const [sectionMgrOpen, setSectionMgrOpen] = useState(false);
  const lastSectionRef = useRef(null);

  // Active tab: "entries" | "heart". Persisted across reloads
  // so users return to the part of the notebook they were last in.
  // ("tools" was removed — placeholder-only panel; left out of the UI
  // but the stored key is still tolerated and falls back to "entries".)
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "entries";
    const stored = window.localStorage.getItem(NOTEBOOK_TAB_KEY);
    return TAB_IDS.includes(stored) ? stored : "entries";
  });
  useEffect(() => {
    try { window.localStorage.setItem(NOTEBOOK_TAB_KEY, activeTab); } catch { /* ignore */ }
  }, [activeTab]);

  // Sticky-tab JS fallback: native `position: sticky` is unreliable when
  // `html, body { overflow-x: hidden }` is in play (it's how some
  // browsers turn body into the scroll container and detach sticky from
  // the viewport). We watch the tabbar's wrapper position via a scroll
  // listener and toggle `data-pinned="1"` so the CSS can promote the
  // tabbar to `position: fixed` when needed.
  const tabbarRef = useRef(null);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const bar = tabbarRef.current;
    if (!sentinel || !bar) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      // Sentinel sits right above the tabbar. When it's pushed above
      // the viewport top, we need to pin.
      const top = sentinel.getBoundingClientRect().top;
      if (top <= 0) bar.setAttribute("data-pinned", "1");
      else bar.removeAttribute("data-pinned");
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [activeTab, user, loading]);

  // ---------- Initial load ----------
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      api.notebookSections().catch(() => ({ items: [] })),
      api.notebookStats().catch(() => null),
    ]).then(([secData, statsData]) => {
      const list = secData.items || [];
      setSections(list);
      setStats(statsData);
      if (list.length > 0 && !activeSectionId) {
        setActiveSectionId(list[0].id);
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ---------- Load entries when section changes / search changes ----------
  useEffect(() => {
    if (!user || !activeSectionId) return;
    const isSectionChange = lastSectionRef.current !== activeSectionId;
    lastSectionRef.current = activeSectionId;
    if (isSectionChange) setFlipState("out");
    // Search debounce: 300 ms for query changes, 250 ms for section flips.
    if (searchQ && !isSectionChange) setSearchLoading(true);
    const t = setTimeout(() => {
      api
        .notebookEntries({
          section_id: searchQ ? undefined : activeSectionId,
          q: searchQ || undefined,
          limit: 100,
        })
        .then((d) => {
          setEntries(d.items || []);
          if (isSectionChange) {
            setFlipState("in");
            setTimeout(() => setFlipState("idle"), 600);
          }
        })
        .catch(() => setEntries([]))
        .finally(() => setSearchLoading(false));
    }, isSectionChange ? 250 : 300);
    return () => clearTimeout(t);
  }, [activeSectionId, searchQ, user]);

  // ---------- Auth gate ----------
  if (!authLoading && !user) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-4">
        <div className="max-w-md text-center space-y-5">
          <NotebookPen className="h-12 w-12 text-gold mx-auto" strokeWidth={1.5} />
          <h1 className="font-heading text-3xl text-foreground">{t("notebook.auth_gate_title")}</h1>
          <p className="text-muted-foreground leading-relaxed">
            {t("notebook.auth_gate_body")}
          </p>
          <Link to="/auth">
            <Button className="sacred-btn" data-testid="notebook-login-cta">
              {t("notebook.auth_gate_cta")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid place-items-center min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  const activeSection = sections.find((s) => s.id === activeSectionId);

  return (
    <div className="container mx-auto px-3 sm:px-4 py-8 max-w-6xl" data-testid="notebook-page">
      {/* Top header + stats */}
      <header className="mb-6">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold mb-1 font-heading">
              {t("notebook.subtitle")}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl text-foreground">
              {t("notebook.title")}
            </h1>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3" data-testid="notebook-stats-strip">
          <Stat icon={Flame} label={t("notebook.stats_streak")} value={stats?.streak_days ?? 0} suffix={t("notebook.stats_streak_days")} />
          <Stat icon={ScrollText} label={t("notebook.stats_verses_learned")} value={stats?.verses_learned ?? 0} />
          <Stat icon={NotebookPen} label={t("notebook.stats_total_entries")} value={stats?.total_entries ?? 0} />
        </div>
      </header>

      {/* === 3-tab navigation — sticky on mobile so the user never loses
              context while scrolling long entry lists. The tab keys
              match the icons in NOTEBOOK_TAB_KEY persistence. === */}
      {/* Sentinel — invisible probe used by the IntersectionObserver
          fallback to know when the tabbar should pin. Lives RIGHT
          above the tabbar so the transition happens exactly where
          native sticky would have engaged. */}
      <div ref={sentinelRef} aria-hidden="true" className="notebook-tabbar-sentinel" />
      <div
        ref={tabbarRef}
        className="notebook-tabbar"
        role="tablist"
        data-testid="notebook-tabbar"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "entries"}
          data-active={activeTab === "entries" ? "1" : "0"}
          onClick={() => setActiveTab("entries")}
          className="notebook-tabbar-btn"
          data-testid="notebook-tabbar-entries"
        >
          <NotebookPen className="h-4 w-4" strokeWidth={1.6} />
          <span>{t("notebook.tab_entries")}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "heart"}
          data-active={activeTab === "heart" ? "1" : "0"}
          onClick={() => setActiveTab("heart")}
          className="notebook-tabbar-btn"
          data-testid="notebook-tabbar-heart"
        >
          <MessageCircleHeart className="h-4 w-4" strokeWidth={1.6} />
          <span>{t("notebook.tab_heart")}</span>
        </button>
        {/* "ארגז הכלים" tab removed — was placeholder-only ("בקרוב").
            Kept ComingSoonPanel + i18n strings in the codebase for
            potential reuse, but no longer reachable from the tabbar. */}
      </div>

      {/* === Tab content. Only one panel is mounted at a time — keeps the
              DOM lean and avoids the entries query running while the
              user is browsing placeholder panels. === */}
      {activeTab === "heart" && (
        <HeartPanel />
      )}

      {/* Notebook stage — only rendered inside the "entries" tab */}
      {activeTab === "entries" && (
      <div className="notebook-stage notebook-tab-panel-anim" data-testid="notebook-stage">
        {!coverOpen ? (
          // Closed cover — shows brand + "open notebook" cue
          <button
            onClick={() => setCoverOpen(true)}
            className="notebook-cover w-full grid place-items-center group"
            data-testid="notebook-open-cover"
            aria-label={t("notebook.open_cover")}
          >
            <div className="text-center">
              <p className="font-heading text-amber-100/80 tracking-[0.4em] text-xs mb-3">
                {t("notebook.cover_label")}
              </p>
              <h2 className="font-heading text-amber-50 text-3xl sm:text-4xl mb-2">
                {user?.name || user?.email?.split("@")[0]}
              </h2>
              <p className="font-verse text-amber-100/60 text-xs mt-4 italic">
                {t("notebook.open_cover")}
              </p>
            </div>
          </button>
        ) : (
          <div className="notebook-layout">
            {/* Bookmark tabs — OUTSIDE the paper on the left so they never cover content */}
            <ul className="notebook-tabs" data-testid="notebook-tabs">
              {sections.map((s) => {
                const Icon = SECTION_ICONS[s.icon] || BookMarked;
                return (
                  <li
                    key={s.id}
                    onClick={() => {
                      setSearchQ("");
                      setActiveSectionId(s.id);
                    }}
                    data-active={s.id === activeSectionId ? "1" : "0"}
                    className={`notebook-tab notebook-tab--${s.color}`}
                    data-testid={`notebook-tab-${s.id}`}
                    title={s.title}
                  >
                    <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                    <span>{s.title}</span>
                  </li>
                );
              })}
              <li
                onClick={() => setSectionMgrOpen(true)}
                className="notebook-tab notebook-tab--slate"
                data-testid="notebook-manage-sections"
                title="ניהול חוצצים"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                <span>חוצץ</span>
              </li>
            </ul>

            {/* Paper */}
            <div className="notebook-paper py-6 sm:py-10 pe-6 sm:pe-10 ps-16 sm:ps-20 flex-1 min-w-0">
              {/* Section toolbar — title + new-entry on one row, search
                  on its own row at mobile (so the input gets the full
                  card width and isn't crushed against the button). */}
              <div className="flex items-center justify-between gap-3 mb-3 relative z-1">
                <h2 className="font-heading text-2xl">
                  {searchQ ? "תוצאות חיפוש" : (activeSection?.title || t("notebook.title"))}
                </h2>
                <Button
                  onClick={() => {
                    setEditingEntry(null);
                    setEditorOpen(true);
                  }}
                  className="sacred-btn shrink-0"
                  size="sm"
                  data-testid="notebook-new-entry"
                >
                  <Plus className="h-4 w-4 ms-1.5" strokeWidth={1.5} />
                  {t("notebook.new_entry")}
                </Button>
              </div>

              <div className="mb-4 sm:mb-6 relative z-1">
                <div className="relative w-full sm:max-w-xs">
                  <Search
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-900/40 pointer-events-none"
                    strokeWidth={1.5}
                  />
                  <Input
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder={t("notebook.search_placeholder")}
                    data-testid="notebook-search"
                    autoComplete="off"
                    dir="rtl"
                    // 16px font on mobile prevents iOS auto-zoom on focus.
                    className="text-base sm:text-sm pr-9 pl-10 bg-white/40 border-amber-900/20 text-amber-950 placeholder:text-amber-900/40 dark:bg-black/30 dark:text-amber-50 dark:border-amber-50/20"
                  />
                  {searchLoading ? (
                    <Loader2
                      data-testid="notebook-search-loading"
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gold"
                      strokeWidth={1.6}
                    />
                  ) : searchQ ? (
                    <button
                      type="button"
                      onClick={() => setSearchQ("")}
                      aria-label="נקה חיפוש"
                      title="נקה חיפוש"
                      data-testid="notebook-search-clear"
                      className="absolute left-1 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-full text-amber-900/60 hover:text-amber-900 hover:bg-amber-900/10 transition-colors"
                    >
                      <X className="h-4 w-4" strokeWidth={1.6} />
                    </button>
                  ) : null}
                </div>

                {/* Search-results counter — visible only when an active
                    query has returned at least one entry. */}
                {searchQ && entries.length > 0 && !searchLoading && (
                  <p
                    className="text-xs text-amber-900/70 dark:text-amber-50/60 mt-2"
                    data-testid="notebook-search-count"
                  >
                    {entries.length === 1
                      ? `תוצאה אחת עבור "${searchQ}"`
                      : `${entries.length} תוצאות עבור "${searchQ}"`}
                  </p>
                )}
              </div>

              {/* Entries — animated by section flip */}
              <div className="notebook-pages relative z-1">
                <div
                  className="notebook-page"
                  data-flipping={flipState === "idle" ? null : flipState}
                  key={`${activeSectionId}-${searchQ}`}
                >
                  {entries.length === 0 ? (
                    !searchLoading && (
                      <EmptyState
                        hasSearch={!!searchQ}
                        query={searchQ}
                        onNew={() => {
                          setEditingEntry(null);
                          setEditorOpen(true);
                        }}
                      />
                    )
                  ) : (
                    <ul className="space-y-4">
                      {entries.map((e) => (
                        <EntryCard
                          key={e.id}
                          entry={e}
                          sections={sections}
                          searchQuery={searchQ}
                          onEdit={() => {
                            setEditingEntry(e);
                            setEditorOpen(true);
                          }}
                          onDelete={async () => {
                            if (!window.confirm("למחוק רשומה זו לצמיתות?")) return;
                            try {
                              await api.notebookDeleteEntry(e.id);
                              setEntries((prev) => prev.filter((x) => x.id !== e.id));
                              toast.success("הרשומה נמחקה");
                            } catch {
                              toast.error("שגיאה במחיקה");
                            }
                          }}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Editor dialog */}
      <EntryEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        entry={editingEntry}
        sections={sections}
        defaultSectionId={activeSectionId}
        // When this dialog is opened from a search result, also pass the
        // current query so the editor can highlight + scroll to the
        // matched word inside the entry body.
        highlightQuery={editingEntry && searchQ ? searchQ : ""}
        onSaved={(saved, isNew) => {
          if (isNew) {
            // refresh list after creating
            api
              .notebookEntries({
                section_id: searchQ ? undefined : activeSectionId,
                q: searchQ || undefined,
                limit: 100,
              })
              .then((d) => setEntries(d.items || []));
            api.notebookStats().then(setStats).catch(() => {});
          } else {
            setEntries((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
          }
        }}
      />

      {/* Section manager dialog */}
      <SectionManager
        open={sectionMgrOpen}
        onClose={() => setSectionMgrOpen(false)}
        sections={sections}
        onChanged={async () => {
          const d = await api.notebookSections();
          setSections(d.items || []);
        }}
      />
    </div>
  );
}

// ----------------------------------------------------------------- //
function Stat({ icon: Icon, label, value, suffix }) {
  return (
    <div className="rounded-sm border border-border/60 bg-card/40 p-3 sm:p-4">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-gold" strokeWidth={1.5} />
      </div>
      <p className="font-heading text-2xl sm:text-3xl text-foreground">
        {value}
        {suffix && <span className="text-xs text-muted-foreground ms-2">{suffix}</span>}
      </p>
    </div>
  );
}

// ----------------------------------------------------------------- //
// Reusable "coming soon" panel rendered in the two placeholder tabs.
// Visual language matches the notebook stage: warm parchment, gold
// frame, generous breathing room, and a small "soon" pill so users
// know we're not lying — the feature is on its way.
function ComingSoonPanel({ icon: Icon, title, subtitle, badge, testId }) {
  return (
    <div
      className="notebook-coming-panel notebook-tab-panel-anim"
      data-testid={testId}
      role="tabpanel"
    >
      <div className="notebook-coming-card">
        <div className="notebook-coming-icon-wrap">
          <Icon className="h-8 w-8" strokeWidth={1.4} />
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl text-foreground mb-2">
          {title}
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground max-w-md leading-relaxed mb-5">
          {subtitle}
        </p>
        <span className="notebook-coming-badge" data-testid={`${testId}-badge`}>
          ✨ {badge}
        </span>
      </div>
    </div>
  );
}

function EmptyState({ hasSearch, query, onNew }) {
  if (hasSearch) {
    // 0-results state for a real query — friendly, with a CTA so the
    // user can pivot from "I can't find it" to "let me write it".
    return (
      <div className="text-center py-16" data-testid="notebook-empty-search">
        <SearchX
          className="h-10 w-10 mx-auto mb-4 text-amber-900/30 dark:text-amber-50/30"
          strokeWidth={1.2}
        />
        <p className="font-heading text-lg text-amber-950 dark:text-amber-50 mb-1.5">
          {query
            ? <>לא נמצאו תוצאות עבור <span className="text-gold">״{query}״</span></>
            : "לא נמצאו תוצאות"}
        </p>
        <p className="text-sm text-amber-900/60 dark:text-amber-50/60 mb-5">
          נסה מילה אחרת או הוסף רשומה חדשה
        </p>
        <Button
          onClick={onNew}
          className="sacred-btn"
          size="sm"
          data-testid="notebook-empty-search-new"
        >
          <Plus className="h-4 w-4 ms-1.5" strokeWidth={1.5} />
          הוסף רשומה חדשה
        </Button>
      </div>
    );
  }
  return (
    <div className="text-center py-16">
      <NotebookPen className="h-10 w-10 mx-auto mb-4 text-amber-900/30 dark:text-amber-50/30" strokeWidth={1.2} />
      <p className="font-heading text-lg text-amber-950 dark:text-amber-50 mb-1">
        דף ריק
      </p>
      <p className="text-sm text-amber-900/60 dark:text-amber-50/60 mb-5">
        התחל לכתוב, או שמור פסוק / פרשנות / שיעור ממקום אחר באפליקציה.
      </p>
      <Button onClick={onNew} className="sacred-btn" size="sm" data-testid="notebook-empty-new">
        <Plus className="h-4 w-4 ms-1.5" strokeWidth={1.5} />
        רשומה חדשה
      </Button>
    </div>
  );
}

function SearchSnippet({ snippet, className = "" }) {
  // Render the search context window: …before <mark>match</mark> after…
  // `snippet` is null/undefined when the backend couldn't locate a
  // matched window (rare: title-only hits with no body). In that case
  // we return null and let the caller render just the title.
  if (!snippet || !snippet.match) return null;
  return (
    <p
      className={`text-sm leading-relaxed text-amber-950/90 dark:text-amber-50/85 font-verse ${className}`}
      dir="rtl"
      data-testid="notebook-search-snippet"
    >
      {snippet.before}
      <mark className="notebook-search-hl">{snippet.match}</mark>
      {snippet.after}
    </p>
  );
}

function EntryCard({ entry, sections, onEdit, onDelete, searchQuery }) {
  const sec = sections.find((s) => s.id === entry.section_id);
  const isSearchResult = !!searchQuery && !!entry.match_snippet;

  // Click delegation — if the user clicks a verse-link card or migrated
  // source block inside the saved note, route them to the reader at
  // that verse. Both element classes carry the same dataset shape
  // (data-book / data-ch / data-v).
  const onNoteClick = (e) => {
    const card =
      e.target.closest?.(".notebook-verse-card") ||
      e.target.closest?.(".notebook-source-block");
    if (!card) return;
    const book = card.dataset.book;
    const ch = card.dataset.ch;
    const v = card.dataset.v;
    if (book && ch) {
      e.preventDefault();
      window.location.href = `/reader/${encodeURIComponent(book)}/${ch}${v ? `?v=${v}` : ""}`;
    }
  };

  // Click anywhere on the card body (away from action buttons) when this
  // is a search result → open the editor and auto-scroll to the first
  // <mark> inside it. Edit / delete / verse-link clicks short-circuit
  // because they handle their own events.
  const onCardClick = (e) => {
    if (!isSearchResult) return;
    // Ignore clicks on interactive descendants
    if (e.target.closest("button, a, .notebook-verse-card")) return;
    onEdit?.();
  };

  return (
    <li
      className="notebook-entry"
      data-testid={`notebook-entry-${entry.id}`}
      onClick={isSearchResult ? onCardClick : undefined}
      style={isSearchResult ? { cursor: "pointer" } : undefined}
    >
      {/* Search snippet — only when this card is rendered as a search
          result with a matched window. Sits at the top so the matched
          context is the first thing the user reads. */}
      {isSearchResult && (
        <SearchSnippet snippet={entry.match_snippet} className="mb-3" />
      )}
      {/* Source line */}
      {(entry.source_ref_he || entry.source_label) && (
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-[11px] uppercase tracking-wider text-amber-900/70 dark:text-amber-100/70 font-heading">
            {entry.source_label && <span>{entry.source_label}</span>}
            {entry.source_label && entry.source_ref_he && <span className="mx-1.5">·</span>}
            {/* Digit→Hebrew-letter conversion is a safety net for
                notebook rows saved BEFORE we switched all writers to
                gematria (Daily share, MikraotShell basket, AI assistant,
                etc.). Newly-saved refs already carry letters so the
                helper is a no-op for them. */}
            {digitsToHebrewGematria(entry.source_ref_he)}
          </p>
          {entry.source_book && entry.source_chapter && entry.source_verse && (
            <Link
              to={`/reader/${bookUrl(entry.source_book)}/${entry.source_chapter}?v=${entry.source_verse}`}
              data-testid={`notebook-entry-source-link-${entry.id}`}
              className="text-amber-900/60 hover:text-amber-900 dark:text-amber-100/60 dark:hover:text-amber-100"
              title="עבור למקור"
            >
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Link>
          )}
        </div>
      )}

      {/* Title */}
      {entry.title && (
        <p className="font-heading text-lg mb-2 text-amber-950 dark:text-amber-50">
          {entry.title}
        </p>
      )}

      {/* Original content */}
      {entry.content_he && (
        <p className="font-verse leading-loose text-amber-950 dark:text-amber-50/90 mb-3" dir="rtl">
          {entry.content_he}
        </p>
      )}
      {!entry.content_he && entry.content_text && (
        <p className="text-sm leading-relaxed text-amber-950/90 dark:text-amber-50/85 mb-3 whitespace-pre-wrap">
          {entry.content_text}
        </p>
      )}

      {/* User note — rendered as HTML because new entries come from
          contentEditable. Old plain-text entries still render fine since
          plain text is valid HTML. */}
      {entry.user_note && (
        <div className="mt-3 pt-3 border-t border-amber-900/15 dark:border-amber-50/15">
          <p className="text-[11px] uppercase tracking-wider text-amber-900/60 dark:text-amber-100/50 mb-1.5 font-heading">
            הערה אישית
          </p>
          <div
            className="text-sm leading-relaxed text-amber-950 dark:text-amber-50/90 notebook-entry-note"
            onClick={onNoteClick}
            dangerouslySetInnerHTML={{ __html: entry.user_note }}
          />
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-amber-900/10 dark:border-amber-50/10">
        <div className="flex items-center gap-2 text-[11px] text-amber-900/60 dark:text-amber-100/50">
          {sec && <span>{sec.title}</span>}
          <span>·</span>
          <span>{new Date(entry.created_at).toLocaleDateString("he-IL")}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            data-testid={`notebook-entry-edit-${entry.id}`}
            className="h-7 w-7 grid place-items-center rounded text-amber-900/60 hover:text-amber-900 hover:bg-amber-900/10 dark:text-amber-100/60 dark:hover:text-amber-100 dark:hover:bg-amber-100/10 transition-colors"
            title="ערוך"
          >
            <Edit3 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button
            onClick={onDelete}
            data-testid={`notebook-entry-delete-${entry.id}`}
            className="h-7 w-7 grid place-items-center rounded text-rose-500/70 hover:text-rose-600 hover:bg-rose-500/10 transition-colors"
            title="מחק"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </li>
  );
}
/**
 * Strip nikud/te'amim and fold final letters for fuzzy match (same
 * shape as the backend `normalize_hebrew`). Side-effect free.
 */
function normalizeHebrewClient(text) {
  if (!text) return "";
  const NIKUD_LO = 0x0591, NIKUD_HI = 0x05C7;
  const FINAL = { "ך": "כ", "ם": "מ", "ן": "נ", "ף": "פ", "ץ": "צ" };
  const drop = "׳'״\"`’‘:·–—-,.;_/";
  let out = "";
  for (const ch of text.normalize("NFKC")) {
    const cp = ch.codePointAt(0);
    if (cp >= NIKUD_LO && cp <= NIKUD_HI) continue;
    if (drop.indexOf(ch) >= 0 || /\s/.test(ch)) {
      if (out.slice(-1) !== " ") out += " ";
      continue;
    }
    out += (FINAL[ch] || ch).toLowerCase();
  }
  return out.trim();
}

/**
 * Find the first text-node occurrence of `rawQuery` (normalized) inside
 * `root`, wrap it in a transient <mark>, and scroll it into view. The
 * mark is removed by the editor's save path so it never persists.
 * Fades to transparent after 2.5 s so it doesn't compete with editing.
 */
function highlightAndScrollFirstMatch(root, rawQuery) {
  if (!root || !rawQuery) return;
  const nq = normalizeHebrewClient(rawQuery);
  if (nq.length < 2) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    if (node.parentElement?.closest?.(".notebook-search-hl-transient")) continue;
    const original = node.textContent;
    if (!original.trim()) continue;
    // Build a parallel normalized string + index map so we can translate
    // the normalized hit back to original-string offsets that
    // surroundContents can use.
    let norm = "";
    const map = [];
    const FINAL = { "ך": "כ", "ם": "מ", "ן": "נ", "ף": "פ", "ץ": "צ" };
    const drop = "׳'״\"`’‘:·–—-,.;_/";
    let lastSpace = -1;
    for (let i = 0; i < original.length; i++) {
      const ch = original[i];
      const cp = ch.codePointAt(0);
      if (cp >= 0x0591 && cp <= 0x05C7) continue;
      if (drop.indexOf(ch) >= 0 || /\s/.test(ch)) {
        if (lastSpace !== norm.length - 1) {
          norm += " ";
          map.push(i);
          lastSpace = norm.length - 1;
        }
        continue;
      }
      norm += (FINAL[ch] || ch).toLowerCase();
      map.push(i);
    }
    const hit = norm.indexOf(nq);
    if (hit < 0) continue;
    const startEnd = hit + nq.length - 1;
    const origStart = map[hit] != null ? map[hit] : 0;
    const origEnd = (map[startEnd] != null ? map[startEnd] : original.length - 1) + 1;
    try {
      const range = document.createRange();
      range.setStart(node, origStart);
      range.setEnd(node, origEnd);
      const mark = document.createElement("mark");
      mark.className = "notebook-search-hl notebook-search-hl-transient";
      range.surroundContents(mark);
      requestAnimationFrame(() => {
        mark.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      setTimeout(() => {
        if (mark.isConnected) {
          mark.style.transition = "background-color 700ms ease";
          mark.style.backgroundColor = "transparent";
        }
      }, 2500);
    } catch {
      /* surroundContents fails on cross-element selections — bail */
    }
    return;
  }
}



// ----------------------------------------------------------------- //
// Build the initial HTML for the edit-mode contentEditable. If the
// entry has a user_note that's already HTML containing the source
// content (new entries / re-edits), we use it as-is. Otherwise we
// MIGRATE the legacy content fields (content_he, content_text) into
// the editor so the user can finally see & edit them in one place.
//
// After save the editor's full innerHTML is persisted to user_note,
// and content_he / content_text are cleared. EntryCard handles both
// shapes — old entries with separate content_he stay readable, edited
// entries store everything in user_note.
function buildEditableHtml(entry) {
  if (!entry) return "";
  const userHtml = entry.user_note || "";
  const alreadyMigrated =
    userHtml.includes("notebook-verse-card") ||
    userHtml.includes("notebook-source-block");
  // If user_note already contains the structured source markup OR there
  // is no legacy content_he/content_text, just hand back what we have.
  if (alreadyMigrated || (!entry.content_he && !entry.content_text)) {
    return userHtml;
  }
  // Migrate legacy entry: prepend a single source block carrying the
  // captured verse/commentary text so the user can read & edit it.
  const safeHe = escapeHtml(entry.content_he || "");
  const safeEn = escapeHtml(entry.content_text || "");
  const safeRef = escapeHtml(entry.source_ref_he || "");
  const safeLabel = escapeHtml(entry.source_label || "");
  // The block carries data-* fields so we can route back to /reader on
  // click (same handler as notebook-verse-card). For verse-type entries
  // we also include book/ch/v so single-verse cards remain navigable.
  const dataAttrs = [
    entry.source_book && `data-book="${escapeAttr(entry.source_book)}"`,
    entry.source_chapter != null && `data-ch="${escapeAttr(String(entry.source_chapter))}"`,
    entry.source_verse != null && `data-v="${escapeAttr(String(entry.source_verse))}"`,
  ].filter(Boolean).join(" ");
  const headerParts = [safeLabel, safeRef].filter(Boolean).join(" · ");
  // Render as a non-editable source block — same visual language as
  // notebook-verse-card so the user immediately understands "this is the
  // saved source content". Use a wrapper <div contenteditable="false">
  // so backspace doesn't accidentally fragment its internals.
  const block = `
<div class="notebook-source-block" contenteditable="false" ${dataAttrs}>
  ${headerParts ? `<div class="notebook-source-block-ref">${headerParts}</div>` : ""}
  ${safeHe ? `<div class="notebook-source-block-he" dir="rtl">${safeHe}</div>` : ""}
  ${safeEn ? `<div class="notebook-source-block-en" dir="ltr">${safeEn}</div>` : ""}
</div>
<p><br></p>
`.trim();
  return block + (userHtml || "");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function escapeAttr(s) {
  return String(s).replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

function EntryEditor({ open, onClose, entry, sections, defaultSectionId, onSaved, highlightQuery }) {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "en" ? "ltr" : "rtl";
  const isEdit = !!entry;
  const [title, setTitle] = useState("");
  const [sectionId, setSectionId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const [verseSearchOpen, setVerseSearchOpen] = useState(false);
  const editorRef = useRef(null);
  // Saved selection range — popovers/dialogs blur the editor, which kills
  // window.getSelection(). Before opening a popover we capture the range
  // here and restore it inside the handlers.
  const savedRangeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setTitle(entry.title || "");
      setSectionId(entry.section_id || defaultSectionId);
      // Render saved note. user_note may be HTML (from contentEditable)
      // or plain text/markdown from older entries — handled both safely.
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = buildEditableHtml(entry);
          // When opened from a search result, locate the matched word
          // inside the editor body and scroll it into view with a soft
          // transient highlight that fades away after 2.5 s. The mark
          // is unwrapped before it can be persisted (see `save`).
          if (highlightQuery) {
            highlightAndScrollFirstMatch(editorRef.current, highlightQuery);
          }
        }
      }, 50);
    } else {
      setTitle("");
      setSectionId(defaultSectionId);
      setTimeout(() => {
        if (editorRef.current) editorRef.current.innerHTML = "";
      }, 50);
    }
  }, [open, entry, defaultSectionId, highlightQuery]);

  // True WYSIWYG via contentEditable + execCommand. The browser handles
  // the actual formatting visually; we save innerHTML as the entry note.
  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  // Snapshot the current selection range so popover/dialog interactions
  // (which blur the editor) don't lose it.
  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    // Only keep if the selection lives inside our editor.
    if (editorRef.current?.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  };
  const restoreSelection = () => {
    const range = savedRangeRef.current;
    if (!range) return null;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    return range;
  };

  // ---- Highlight: manual <span> wrap/unwrap. We avoid
  // execCommand("hiliteColor") because Safari ≤14 silently drops it and
  // because we want the colour to survive copy-paste of saved HTML.
  const applyHighlight = (color) => {
    editorRef.current?.focus();
    const range = restoreSelection();
    if (!range || range.collapsed) {
      setHighlightOpen(false);
      return;
    }
    // If the range is fully inside an existing highlight of the SAME
    // colour, treat the click as "remove highlight" (toggle behaviour).
    const ancestor = range.commonAncestorContainer;
    const parentHL =
      (ancestor.nodeType === 1 ? ancestor : ancestor.parentElement)
        ?.closest?.(".notebook-highlight");
    if (parentHL && parentHL.getAttribute("data-color") === color) {
      // Unwrap: replace the <span> with its children
      const parent = parentHL.parentNode;
      while (parentHL.firstChild) parent.insertBefore(parentHL.firstChild, parentHL);
      parent.removeChild(parentHL);
    } else {
      // Wrap the selected range in a fresh highlight span.
      const span = document.createElement("span");
      span.className = "notebook-highlight";
      span.setAttribute("data-color", color);
      // Inline style as a defensive copy — survives if the CSS file
      // ever fails to load.
      const colourMap = { yellow: "#FFF4B8", green: "#D4F4D4", orange: "#FFE0B8" };
      span.style.backgroundColor = colourMap[color] || "#FFF4B8";
      try {
        // surroundContents throws if the range spans multiple element
        // boundaries — in that case fall back to extract + wrap.
        range.surroundContents(span);
      } catch {
        const frag = range.extractContents();
        span.appendChild(frag);
        range.insertNode(span);
      }
    }
    savedRangeRef.current = null;
    setHighlightOpen(false);
  };

  // ---- Verse link card insertion ----------------------------------
  // Builds and inserts a contenteditable=false card representing a
  // single verse. The X button is inline; clicking it removes the card.
  // After insertion the caret is placed on a fresh paragraph beneath the
  // card so the user can keep writing.
  const insertVerseCard = (verse) => {
    editorRef.current?.focus();
    const range = restoreSelection() || (() => {
      // No previous selection — append to end of editor.
      const r = document.createRange();
      r.selectNodeContents(editorRef.current);
      r.collapse(false);
      return r;
    })();

    const refHe = `${verse.book_he || verse.book} ${numberToHebrewLetter(verse.chapter)}:${numberToHebrewLetter(verse.verse)}`;
    const card = document.createElement("div");
    card.setAttribute("contenteditable", "false");
    card.className = "notebook-verse-card";
    card.dataset.ref = verse.ref || `${verse.book} ${verse.chapter}:${verse.verse}`;
    card.dataset.book = verse.book;
    card.dataset.ch = String(verse.chapter);
    card.dataset.v = String(verse.verse);
    card.innerHTML = `
      <button type="button" class="notebook-verse-card-close" aria-label="${escapeAttr(t("notebook_internals.remove_card"))}" tabindex="-1">×</button>
      <div class="notebook-verse-card-ref">${refHe}</div>
      <div class="notebook-verse-card-text" dir="rtl"></div>
    `;
    // Set verse text via textContent to avoid HTML-injection from API.
    card.querySelector(".notebook-verse-card-text").textContent = verse.he || verse.en || "";

    // Insert the card at the caret. Then add an empty <p> after it and
    // move the caret into that paragraph so typing continues naturally.
    range.deleteContents();
    range.insertNode(card);
    const tail = document.createElement("p");
    tail.appendChild(document.createElement("br"));
    card.parentNode.insertBefore(tail, card.nextSibling);
    const newRange = document.createRange();
    newRange.setStart(tail, 0);
    newRange.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(newRange);

    savedRangeRef.current = null;
    setVerseSearchOpen(false);
  };

  // Handle clicks inside the editor: clicks on the verse-card close X
  // delete the card; clicks on the card body are absorbed (we don't
  // navigate while editing).
  const onEditorClick = (e) => {
    if (e.target.classList?.contains("notebook-verse-card-close")) {
      e.preventDefault();
      e.stopPropagation();
      const card = e.target.closest(".notebook-verse-card");
      card?.remove();
    }
  };

  // Handle Backspace at the start of a line that is preceded by a verse
  // card → delete the whole card. Without this, the user is "trapped"
  // because the card is contenteditable=false.
  const onEditorKeyDown = (e) => {
    if (e.key !== "Backspace") return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return;
    // Only act if caret is at the very start of its container.
    if (range.startOffset !== 0) return;
    // Walk up to find the previous sibling at the editor root.
    let node = range.startContainer;
    while (node && node !== editorRef.current && node.parentNode !== editorRef.current) {
      if (node.previousSibling) break;
      node = node.parentNode;
    }
    const prev = node?.previousSibling;
    if (prev?.classList?.contains?.("notebook-verse-card")) {
      e.preventDefault();
      prev.remove();
    }
  };

  const save = async () => {
    setSaving(true);
    // Strip any transient search-highlight marks before persisting so
    // the user's saved HTML never contains them. Unwrap rather than
    // remove — we want to keep the inner text intact.
    if (editorRef.current) {
      editorRef.current.querySelectorAll("mark.notebook-search-hl-transient").forEach((m) => {
        while (m.firstChild) m.parentNode.insertBefore(m.firstChild, m);
        m.remove();
      });
    }
    const noteHtml = editorRef.current?.innerHTML?.trim() || "";
    // If the user edited a migrated legacy entry, the notebook-source-block
    // now lives inside user_note. Clear the legacy content_he/content_text
    // so we don't duplicate it on screen (EntryCard renders content_he then
    // user_note — without this we'd show the verse twice).
    const wasMigrated =
      isEdit &&
      (entry?.content_he || entry?.content_text) &&
      (noteHtml.includes("notebook-source-block") ||
        noteHtml.includes("notebook-verse-card"));
    try {
      if (isEdit) {
        const updated = await api.notebookUpdateEntry(entry.id, {
          title: title || undefined,
          user_note: noteHtml || undefined,
          section_id: sectionId || undefined,
          // Setting to empty string is intentional — clears the legacy
          // duplicate. The backend treats "" as a real value (vs.
          // undefined which means "don't touch").
          ...(wasMigrated ? { content_he: "", content_text: "" } : {}),
        });
        toast.success(t("notebook_internals.entry_saved"));
        onSaved?.(updated, false);
      } else {
        const created = await api.notebookCreateEntry({
          source_type: "freeform",
          section_id: sectionId,
          title: title || undefined,
          user_note: noteHtml || undefined,
        });
        toast.success(t("notebook_internals.entry_saved"));
        onSaved?.(created, true);
      }
      onClose?.();
    } catch (err) {
      toast.error(t("notebook_internals.entry_save_failed"), {
        description: err.response?.data?.detail || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent
        className="max-w-2xl [&>*]:min-w-0 max-h-[90vh] overflow-y-auto"
        dir={dir}
        data-testid="notebook-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-gold" strokeWidth={1.5} />
            {isEdit ? t("notebook_internals.editor_edit") : t("notebook_internals.editor_new")}
          </DialogTitle>
          <DialogDescription className="text-start text-xs">
            {t("notebook_internals.editor_desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("notebook_internals.editor_title_placeholder")}
            dir={dir}
            data-testid="notebook-editor-title"
            className="font-heading text-lg"
          />

          {/* Toolbar — icon-only buttons. Manual highlight wrapping
              (not execCommand) for cross-browser support. */}
          <div className="notebook-editor-toolbar">
            <ToolBtn
              icon={Heading2}
              label={t("notebook_internals.tool_heading")}
              onClick={() => exec("formatBlock", "h2")}
              data-testid="notebook-tool-heading"
            />
            <span className="notebook-editor-toolbar-sep" aria-hidden />
            <ToolBtn
              icon={Bold}
              label={t("notebook_internals.tool_bold")}
              onClick={() => exec("bold")}
              data-testid="notebook-tool-bold"
            />
            <ToolBtn
              icon={Italic}
              label={t("notebook_internals.tool_italic")}
              onClick={() => exec("italic")}
              data-testid="notebook-tool-italic"
            />
            <span className="notebook-editor-toolbar-sep" aria-hidden />
            <ToolBtn
              icon={List}
              label={t("notebook_internals.tool_list")}
              onClick={() => exec("insertUnorderedList")}
              data-testid="notebook-tool-list"
            />
            <ToolBtn
              icon={ListOrdered}
              label={t("notebook_internals.tool_ordered")}
              onClick={() => exec("insertOrderedList")}
              data-testid="notebook-tool-ordered-list"
            />
            <span className="notebook-editor-toolbar-sep" aria-hidden />
            <ToolBtn
              icon={Quote}
              label={t("notebook_internals.tool_quote")}
              onClick={() => exec("formatBlock", "blockquote")}
              data-testid="notebook-tool-blockquote"
            />
            <Popover
              open={highlightOpen}
              onOpenChange={(o) => {
                if (o) saveSelection();
                setHighlightOpen(o);
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  title={t("notebook_internals.tool_highlight")}
                  aria-label={t("notebook_internals.tool_highlight")}
                  className="notebook-editor-tool-btn"
                  data-testid="notebook-tool-highlight"
                >
                  <Highlighter className="h-[18px] w-[18px]" strokeWidth={1.6} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="center"
                className="w-auto p-2 flex items-center gap-2"
                dir={dir}
              >
                {[
                  { id: "yellow", bg: "#FFF4B8", label: t("notebook_internals.hl_yellow") },
                  { id: "green",  bg: "#D4F4D4", label: t("notebook_internals.hl_green") },
                  { id: "orange", bg: "#FFE0B8", label: t("notebook_internals.hl_orange") },
                ].map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    title={c.label}
                    aria-label={t("notebook_internals.tool_highlight_named", { name: c.label })}
                    className="notebook-highlight-swatch"
                    style={{ backgroundColor: c.bg }}
                    data-testid={`notebook-highlight-${c.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyHighlight(c.id)}
                  />
                ))}
              </PopoverContent>
            </Popover>
            <button
              type="button"
              title={t("notebook_internals.tool_verse_link")}
              aria-label={t("notebook_internals.tool_verse_link")}
              className="notebook-editor-tool-btn"
              data-testid="notebook-tool-verse-link"
              onMouseDown={(e) => {
                // Capture selection BEFORE the dialog steals focus.
                e.preventDefault();
                saveSelection();
              }}
              onClick={() => setVerseSearchOpen(true)}
            >
              <BookOpen className="h-[18px] w-[18px]" strokeWidth={1.6} />
            </button>
            <span className="notebook-editor-toolbar-sep" aria-hidden />
            <span className="flex-1 min-w-0" />
            <select
              value={sectionId || ""}
              onChange={(e) => setSectionId(e.target.value)}
              data-testid="notebook-editor-section-select"
              className="flex-shrink-0 text-xs bg-card border border-border/60 rounded-sm px-2 py-1.5 min-h-[32px]"
              dir={dir}
            >
              {sections.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </div>

          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            dir={dir}
            data-placeholder={t("notebook_internals.editor_body_placeholder")}
            data-testid="notebook-editor-textarea"
            className="notebook-editor-wysiwyg"
            onClick={onEditorClick}
            onKeyDown={onEditorKeyDown}
          />

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>{t("notebook_internals.cancel")}</Button>
            <Button onClick={save} disabled={saving || !sectionId} className="sacred-btn" data-testid="notebook-editor-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin ms-2" /> : <Save className="h-4 w-4 ms-2" strokeWidth={1.5} />}
              {t("notebook_internals.save")}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Verse search dialog — rendered alongside the editor Dialog. */}
      <VerseSearchDialog
        open={verseSearchOpen}
        onClose={() => {
          savedRangeRef.current = null;
          setVerseSearchOpen(false);
        }}
        onPick={insertVerseCard}
      />
    </Dialog>
  );
}

function VerseSearchDialog({ open, onClose, onPick }) {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "en" ? "ltr" : "rtl";
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);

  // Reset state on open/close and auto-focus the input on open.
  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
      setSearched(false);
      return;
    }
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  // Debounced search — fire 300 ms after the last keystroke, only if
  // the trimmed query is at least 2 characters long (per spec).
  useEffect(() => {
    if (!open) return undefined;
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearched(false);
      return undefined;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const data = await api.search(trimmed);
        // Keep only Tanakh hits — commentary refs don't make sense as a
        // verse-link card.
        const verses = (data?.results || []).filter((r) => r.kind === "tanakh").slice(0, 5);
        setResults(verses);
      } catch {
        setResults([]);
      } finally {
        setSearched(true);
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [q, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-md [&>*]:min-w-0 max-h-[90vh] overflow-y-auto"
        dir={dir}
        data-testid="notebook-verse-search-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-gold" strokeWidth={1.5} />
            {t("notebook_internals.verse_link_title")}
          </DialogTitle>
          <DialogDescription className="text-start text-xs">
            {t("notebook_internals.verse_link_desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("notebook_internals.verse_search_placeholder")}
            dir={dir}
            data-testid="notebook-verse-search-input"
            className="text-base"
            autoComplete="off"
          />

          <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1">
            {q.trim().length < 2 ? (
              <p className="text-xs text-muted-foreground py-6 text-center">
                {t("notebook_internals.verse_search_hint")}
              </p>
            ) : loading ? (
              <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("notebook_internals.verse_searching")}
              </div>
            ) : searched && results.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="notebook-verse-search-empty">
                {t("notebook_internals.verse_not_found")}
              </p>
            ) : (
              <ul className="space-y-1.5" data-testid="notebook-verse-search-results">
                {results.map((r) => (
                  <li key={r.ref}>
                    <button
                      type="button"
                      onClick={() => onPick(r)}
                      className="w-full text-start rounded-sm border border-border/60 hover:border-gold/50 bg-card/50 hover:bg-gold/5 px-3 py-2.5 transition-all"
                      data-testid={`notebook-verse-search-result-${r.ref.replace(/\s+/g, '-')}`}
                    >
                      <div className="text-[11px] font-latin tracking-wide text-gold/80 mb-1">
                        {r.book_he || r.book} {numberToHebrewLetter(r.chapter)}:{numberToHebrewLetter(r.verse)}
                      </div>
                      <div className="text-sm font-verse text-foreground line-clamp-2 leading-relaxed">
                        {r.he || r.en}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToolBtn({ onClick, icon: Icon, label, className = "", ...rest }) {
  // Icon-only button with native tooltip via `title`. Uses mousedown
  // preventDefault so the contentEditable surface keeps its selection
  // when the user clicks a formatting button.
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`notebook-editor-tool-btn ${className}`}
      {...rest}
    >
      {Icon ? <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} /> : label}
    </button>
  );
}

function SectionManager({ open, onClose, sections, onChanged }) {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "en" ? "ltr" : "rtl";
  const [newTitle, setNewTitle] = useState("");
  const [newColor, setNewColor] = useState("amber");
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(null); // section.id

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await api.notebookCreateSection({ title: newTitle.trim(), color: newColor });
      setNewTitle("");
      setNewColor("amber");
      await onChanged?.();
      toast.success(t("notebook_internals.section_added"));
    } catch {
      toast.error(t("notebook_internals.section_add_failed"));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(t("notebook_internals.section_delete_confirm", { name: s.title }))) return;
    try {
      await api.notebookDeleteSection(s.id);
      await onChanged?.();
      toast.success(t("notebook_internals.section_deleted"));
    } catch {
      toast.error(t("notebook_internals.section_delete_failed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent dir={dir} data-testid="notebook-section-manager">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{t("notebook_internals.section_manager_title")}</DialogTitle>
          <DialogDescription className="text-start text-xs">
            {t("notebook_internals.section_manager_desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Existing sections */}
          <ul className="space-y-2">
            {sections.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded border border-border/60"
                data-testid={`section-manage-row-${s.id}`}
              >
                {renaming === s.id ? (
                  <RenameInline
                    section={s}
                    onSave={async (title, color) => {
                      try {
                        await api.notebookUpdateSection(s.id, { title, color });
                        await onChanged?.();
                      } catch { toast.error(t("notebook_internals.section_rename_failed")); }
                      setRenaming(null);
                    }}
                    onCancel={() => setRenaming(null)}
                  />
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className={`h-3 w-3 rounded-full bg-${s.color}-400`} />
                      <span className="font-heading text-sm">{s.title}</span>
                      {s.is_default && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("notebook_internals.section_default_label")}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setRenaming(s.id)}
                        className="p-1 text-muted-foreground hover:text-gold transition-colors"
                        title={t("notebook_internals.section_rename")}
                        data-testid={`section-rename-${s.id}`}
                      >
                        <Edit3 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        className="p-1 text-muted-foreground hover:text-rose-500 transition-colors"
                        title={t("notebook_internals.section_delete")}
                        data-testid={`section-delete-${s.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>

          {/* Add new */}
          <div className="border-t border-border/60 pt-4 space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-heading">{t("notebook_internals.section_add_new")}</p>
            <div className="flex items-center gap-2">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t("notebook_internals.section_name_placeholder")}
                dir={dir}
                data-testid="section-new-title"
              />
              <select
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="text-xs bg-card border border-border/60 rounded-sm px-2 py-2"
                dir={dir}
                data-testid="section-new-color"
              >
                {COLOR_OPTIONS.map((c) => (
                  <option key={c.id} value={c.id}>{t(`notebook_internals.color_${c.id}`)}</option>
                ))}
              </select>
              <Button
                onClick={handleCreate}
                disabled={!newTitle.trim() || creating}
                size="sm"
                className="sacred-btn shrink-0"
                data-testid="section-new-create"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" strokeWidth={1.5} />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RenameInline({ section, onSave, onCancel }) {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "en" ? "ltr" : "rtl";
  const [title, setTitle] = useState(section.title);
  const [color, setColor] = useState(section.color);
  return (
    <div className="flex items-center gap-2 flex-1">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        dir={dir}
        autoFocus
        className="text-sm h-8"
      />
      <select
        value={color}
        onChange={(e) => setColor(e.target.value)}
        className="text-xs bg-card border border-border/60 rounded-sm px-2 py-1"
      >
        {COLOR_OPTIONS.map((c) => (
          <option key={c.id} value={c.id}>{t(`notebook_internals.color_${c.id}`)}</option>
        ))}
      </select>
      <button onClick={() => onSave(title, color)} className="p-1 text-emerald-500"><Check className="h-4 w-4" /></button>
      <button onClick={onCancel} className="p-1 text-muted-foreground"><X className="h-4 w-4" /></button>
    </div>
  );
}
